import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, ArrowLeft, Play, Eye, Flame, Loader2, TrendingUp, Hash, Share2 } from 'lucide-react';
import { API } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import { toast } from '../hooks/use-toast';

const Trends = () => {
  const { lang } = useLang();
  const [videos, setVideos] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  const shareVideo = async (e, id) => {
    e.preventDefault(); e.stopPropagation();
    try { await navigator.clipboard.writeText(`${window.location.origin}/v/${id}`); toast({ title: lang === 'sv' ? 'Länk kopierad!' : 'تم نسخ الرابط!' }); } catch (_) {}
  };

  useEffect(() => {
    (async () => {
      try {
        const [v, t] = await Promise.all([
          axios.get(`${API}/trends`),
          axios.get(`${API}/tags/trending`)
        ]);
        setVideos(v.data.videos || []);
        setTags(t.data.tags || []);
      } catch (_) {}
      finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        <div className="flex items-center justify-between mb-8">
          <a href="/" className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900 text-[13.5px] font-semibold"><ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {lang === 'sv' ? 'Startsidan' : 'الرئيسية'}</a>
          <div className="flex items-center gap-2"><div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div><span className="font-black text-neutral-900 text-[16px]">CapCute</span></div>
        </div>

        <div className="mb-10">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-500 to-orange-500 rounded-full px-3 py-1 mb-3">
            <Flame className="w-3.5 h-3.5 text-white" fill="white" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-white">{lang === 'sv' ? 'Trendar' : 'رائج'}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-[-0.02em] text-neutral-900">{lang === 'sv' ? 'Populärast just nu' : 'الأكثر شيوعاً الآن'}</h1>
          <p className="text-[15px] text-neutral-600 mt-3">{lang === 'sv' ? 'Mest visade och reagerade på senaste 7 dagarna' : 'الأكثر مشاهدة وتفاعلاً خلال آخر 7 أيام'}</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>
        ) : (
          <>
            {tags.length > 0 && (
              <div className="mb-10">
                <div className="flex items-center gap-2 mb-4">
                  <Hash className="w-4 h-4 text-cyan-600" />
                  <h2 className="font-bold text-neutral-900">{lang === 'sv' ? 'Trendande taggar' : 'الوسوم الرائجة'}</h2>
                </div>
                <div className="flex flex-wrap gap-2">
                  {tags.map(t => (
                    <a key={t.tag} href={`/t/${encodeURIComponent(t.tag)}`} className="group h-10 px-4 rounded-full bg-white border border-neutral-200 hover:border-neutral-900 hover:bg-neutral-900 hover:text-white transition-colors flex items-center gap-2 text-[13px] font-bold text-neutral-800">
                      <Hash className="w-3.5 h-3.5 text-cyan-500 group-hover:text-cyan-300" />
                      <span>{t.tag}</span>
                      <span className="text-[11px] font-semibold opacity-60">{t.views}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
            {videos.length === 0 ? (
              <div className="bg-white rounded-3xl border border-neutral-200 p-14 text-center">
                <TrendingUp className="w-14 h-14 mx-auto text-neutral-300 mb-4" />
                <h3 className="text-xl font-bold text-neutral-900 mb-2">{lang === 'sv' ? 'Inga trender ännu' : 'لا توجد اتجاهات بعد'}</h3>
                <p className="text-[14px] text-neutral-500">{lang === 'sv' ? 'Skapa och dela videor för att synas här!' : 'أنشئ وشارك فيديوهات لتظهر هنا!'}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {videos.map((v, i) => (
                  <a key={v.id} href={`/v/${v.id}`} className="group relative bg-white rounded-2xl overflow-hidden border border-neutral-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                    <div className="relative aspect-[4/5] bg-neutral-900 overflow-hidden">
                      {v.thumb_path ? (
                        <img src={`${API}/files/${v.thumb_path}`} alt={v.title} className="w-full h-full object-cover" />
                      ) : (<div className="w-full h-full bg-gradient-to-br from-cyan-500 to-fuchsia-500" />)}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent" />
                      <div className={`absolute top-3 left-3 rtl:left-auto rtl:right-3 w-9 h-9 rounded-xl flex items-center justify-center font-black text-[15px] ${i === 0 ? 'bg-amber-400 text-black' : i === 1 ? 'bg-neutral-200 text-black' : i === 2 ? 'bg-orange-500 text-white' : 'bg-black/60 backdrop-blur text-white'}`}>#{i + 1}</div>
                      <button data-testid={`share-trend-${v.id}`} onClick={(e) => shareVideo(e, v.id)} title={lang === 'sv' ? 'Dela' : 'مشاركة'} className="absolute top-3 right-3 rtl:right-auto rtl:left-3 w-9 h-9 rounded-xl bg-black/60 backdrop-blur hover:bg-white hover:text-black text-white flex items-center justify-center transition-colors z-10"><Share2 className="w-4 h-4" /></button>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-xl"><Play className="w-6 h-6 fill-black text-black ml-0.5" /></div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <h3 className="font-bold text-[14px] text-white truncate mb-1.5 tracking-tight">{v.title}</h3>
                        <div className="flex items-center gap-2 text-white/90">
                          <span className="text-[11px] font-bold flex items-center gap-1"><Eye className="w-3 h-3" /> {v.view_count || 0}</span>
                          <span className="text-[11px] font-bold flex items-center gap-1"><Flame className="w-3 h-3 text-orange-400" fill="currentColor" /> {v.reactions_total || 0}</span>
                        </div>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Trends;
